import React from 'react';
import PropTypes from 'prop-types';
import { flexRender } from '@tanstack/react-table';
import styles from './AssignmentHeader.module.css';

export function AssignmentHeader({
  headerGroup,
  stickyColumns: _stickyColumns,
  scrollableColumns,
  virtualColumns,
  paddingLeft,
  paddingRight,
  columnIndexMap,
  focusedCell,
  onCellClick,
  getAriaSortValue,
}) {
  return (
    <tr
      key={headerGroup.id}
      role="row"
      aria-rowindex={2}
      style={{ height: 95 }}
    >
      {/* Render sticky name column */}
      {headerGroup.headers
        .filter((header) => header.id === 'name')
        .map((header) => {
          const isFocused =
            focusedCell?.rowIndex === -2 && focusedCell?.columnId === header.id;
          return (
            <th
              key={header.id}
              className={`${styles.stickyNameCell} ${isFocused ? styles.focusedCell : ''}`}
              style={{
                width: 250,
                minWidth: 250,
                maxWidth: 250,
                position: 'sticky',
                left: 0,
              }}
              role="columnheader"
              scope="col"
              tabIndex={isFocused ? 0 : -1}
              aria-colindex={1}
              aria-sort={getAriaSortValue?.(header.id)}
              data-row-index={-2}
              data-column-id={header.id}
              data-grid-id={`r1-c${columnIndexMap?.get(header.id) ?? 0}`}
              onClick={() => onCellClick?.(-2, header.id)}
            >
              {header.isPlaceholder ? (
                <span className={styles.srOnly}>Empty column</span>
              ) : (
                flexRender(header.column.columnDef.header, header.getContext())
              )}
            </th>
          );
        })}

      {/* Padding for horizontal scroll - always render for stable layout */}
      <th
        aria-hidden="true"
        style={{
          width: paddingLeft,
          minWidth: paddingLeft,
          maxWidth: paddingLeft,
          padding: 0,
          border: 'none',
        }}
      />

      {/* Render virtual columns */}
      {virtualColumns.map((virtualColumn) => {
        const column = scrollableColumns[virtualColumn.index];
        const header = headerGroup.headers.find((h) => h.id === column.id);

        if (!header) return <th key={`empty-${virtualColumn.index}`} />;

        // Use gray background for Course Total and Category Total columns
        const isTotalColumn =
          header.id === 'courseTotal' ||
          header.id.startsWith('category-total-');
        // Check if this assignment has a clickable link (from column meta)
        const meta = header.column.columnDef.meta;
        const hasClickableLink = meta?.hasClickableLink;
        const isFocused =
          focusedCell?.rowIndex === -2 && focusedCell?.columnId === header.id;
        const cellClasses = [
          isTotalColumn ? styles.totalCell : styles.assignmentCell,
          hasClickableLink ? styles.hasLink : '',
          isFocused ? styles.focusedCell : '',
        ]
          .filter(Boolean)
          .join(' ');

        // Build aria-label for accessibility
        let ariaLabel;
        if (header.id === 'courseTotal') {
          ariaLabel = 'Course Total';
        } else if (header.id === 'studentId') {
          ariaLabel = 'Student ID';
        } else if (header.id.startsWith('category-total-')) {
          ariaLabel = `${meta?.categoryName || 'Category'} Total`;
        } else if (meta?.assignmentName) {
          ariaLabel = meta.assignmentName;
        }

        return (
          <th
            key={header.id}
            className={cellClasses}
            style={{
              width: `${virtualColumn.size}px`,
              minWidth: `${virtualColumn.size}px`,
              maxWidth: `${virtualColumn.size}px`,
            }}
            role="columnheader"
            scope="col"
            tabIndex={isFocused ? 0 : -1}
            aria-colindex={(columnIndexMap?.get(header.id) ?? 0) + 1}
            aria-sort={getAriaSortValue?.(header.id)}
            aria-label={ariaLabel}
            data-row-index={-2}
            data-column-id={header.id}
            data-grid-id={`r1-c${columnIndexMap?.get(header.id) ?? 0}`}
            onClick={() => onCellClick?.(-2, header.id)}
          >
            {header.isPlaceholder ? (
              <span className={styles.srOnly}>Empty column</span>
            ) : (
              flexRender(header.column.columnDef.header, header.getContext())
            )}
          </th>
        );
      })}

      {/* Padding for horizontal scroll (right) - for columns after visible ones */}
      <th
        aria-hidden="true"
        style={{
          width: paddingRight,
          minWidth: paddingRight,
          maxWidth: paddingRight,
          padding: 0,
          border: 'none',
        }}
      />
    </tr>
  );
}

AssignmentHeader.propTypes = {
  headerGroup: PropTypes.object.isRequired,
  stickyColumns: PropTypes.array,
  scrollableColumns: PropTypes.array.isRequired,
  virtualColumns: PropTypes.array.isRequired,
  paddingLeft: PropTypes.number.isRequired,
  paddingRight: PropTypes.number.isRequired,
  columnIndexMap: PropTypes.instanceOf(Map),
  focusedCell: PropTypes.shape({
    rowIndex: PropTypes.number,
    columnId: PropTypes.string,
  }),
  onCellClick: PropTypes.func,
  getAriaSortValue: PropTypes.func,
};

AssignmentHeader.defaultProps = {
  stickyColumns: [],
  columnIndexMap: null,
  focusedCell: null,
  onCellClick: () => {},
  getAriaSortValue: null,
};
